import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, PRIVACY_LEVELS, imageUrl } from "@/lib/api";
import { resizeImageForUpload, makeThumb } from "@/lib/imageResize";
import { uploadVideoChunked, isVideoFile } from "@/lib/videoUpload";
import CropModal from "@/components/CropModal";
import { UploadSimple, CheckCircle, SpinnerGap, XCircle, ImageSquare, GoogleLogo, LinkSimple, ArrowSquareOut, Sparkle, ArrowRight, ShieldCheck, Warning, Camera, Crop, FilmSlate, PlayCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const REUSE_OPTIONS = [
  { v: "private", l: "Private (only me)" },
  { v: "family_only", l: "Family only" },
  { v: "specific_sites", l: "Specific SocialPulse sites" },
  { v: "all_socialpulse", l: "All SocialPulse properties" },
  { v: "public_display", l: "Public display" },
  { v: "public_attribution", l: "Public reuse with attribution" },
  { v: "commercial", l: "Commercial use permitted" },
  { v: "embed_only", l: "Original-link / embed only" },
];
const SITES = [
  { v: "socialpulse", l: "SocialPulse" }, { v: "indianfamilytree", l: "FamilyTree" },
  { v: "alumnipulse", l: "AlumniPulse" }, { v: "travelerpulse", l: "TravelerPulse" },
  { v: "foodpulse", l: "FoodPulse" }, { v: "naturepulse", l: "NaturePulse" },
];

export default function Upload() {
  const nav = useNavigate();
  const inputRef = useRef();
  const cameraRef = useRef();
  const [params] = useSearchParams();
  const [privacy, setPrivacy] = useState("private");
  const [album, setAlbum] = useState("");
  const [items, setItems] = useState([]); // {file, status, id, isVideo, progress}
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropIndex, setCropIndex] = useState(null);

  // PWA "Take a photo" shortcut (/upload?camera=1) auto-opens the camera.
  useEffect(() => {
    if (params.get("camera") === "1") {
      const t = setTimeout(() => cameraRef.current?.click(), 350);
      return () => clearTimeout(t);
    }
  }, [params]);

  // Shared batch Rights & Reuse (applies to bulk drag-drop + Google Photos)
  const [reuse, setReuse] = useState("private");
  const [sites, setSites] = useState([]);
  const [reviewEach, setReviewEach] = useState(false);
  const [makePublic, setMakePublic] = useState(false);
  const [quality, setQuality] = useState("balanced"); // web | balanced | high
  const QUALITY_PRESETS = {
    web: { maxEdge: 1600, q: 0.6, label: "Web — smallest & fastest" },
    balanced: { maxEdge: 1920, q: 0.72, label: "Balanced — recommended" },
    high: { maxEdge: 2560, q: 0.85, label: "High — sharpest, larger" },
  };
  const toggleSite = (v) => setSites((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const reuseSites = () => (reuse === "private" || reuse === "family_only" ? "" : sites.join(","));
  const goGuidedQueue = (idList) => {
    if (!idList?.length) return;
    const [first, ...rest] = idList;
    nav(`/upload/guided?id=${first}${rest.length ? `&queue=${rest.join(",")}` : ""}`);
  };

  // Import-from-link state
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importNotice, setImportNotice] = useState(null); // {type, text}

  const doImport = async () => {
    const url = importUrl.trim();
    if (!url) { toast.error("Paste a photo or post link first."); return; }
    setImporting(true);
    setImportNotice(null);
    try {
      const { data } = await api.post("/photos/import-url", { url });
      toast.success("Link imported — finishing details…");
      nav(`/upload/guided?id=${data.id}`);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 422) {
        setImportNotice({ type: "wall", text: detail || "This looks like a private/social post. Download the photo and use the guided uploader instead." });
      } else {
        setImportNotice({ type: "error", text: detail || "Could not import that link." });
      }
    } finally { setImporting(false); }
  };

  // Google Photos state
  const [gp, setGp] = useState({ configured: false, connected: false });
  const [gpSession, setGpSession] = useState(null); // {session_id, picker_uri, media_items_set}
  const [gpBusy, setGpBusy] = useState(false);

  const loadGp = () => api.get("/google/status").then((r) => setGp(r.data)).catch(() => {});
  useEffect(() => {
    loadGp();
    const g = params.get("google");
    if (g === "connected") { toast.success("Google Photos connected"); loadGp(); }
    if (g === "error") toast.error("Could not connect Google Photos");
  }, []); // eslint-disable-line

  const gpConnect = async () => {
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google Photos not configured yet");
    }
  };
  const gpDisconnect = async () => { await api.post("/google/disconnect"); setGpSession(null); loadGp(); toast.success("Disconnected"); };
  const gpPick = async () => {
    setGpBusy(true);
    try {
      const { data } = await api.post("/google/picker/session");
      setGpSession(data);
      window.open(data.picker_uri, "_blank", "noopener");
      toast.info("Pick photos in the Google tab, then click 'Import selected' here.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start picker"); }
    setGpBusy(false);
  };
  const gpImport = async () => {
    if (!gpSession) return;
    setGpBusy(true);
    try {
      const st = await api.get(`/google/picker/session/${gpSession.session_id}`);
      if (!st.data.media_items_set) { toast.warning("No photos selected yet in Google. Pick some first."); setGpBusy(false); return; }
      const fd = new FormData(); fd.append("privacy", privacy); fd.append("album", album || "Google Photos");
      fd.append("reuse_permission", reuse); fd.append("usable_on_sites", reuseSites());
      fd.append("make_public", makePublic ? "true" : "false");
      const { data } = await api.post(`/google/picker/import/${gpSession.session_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      setGpSession(null);
      if (reviewEach && data.ids?.length) { goGuidedQueue(data.ids); return; }
      if (data.ids?.length) { nav("/upload/imported", { state: { ids: data.ids } }); return; }
      nav("/library");
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setGpBusy(false);
  };

  const addFiles = async (fileList) => {
    const arr = Array.from(fileList).filter(
      (f) => f.type?.startsWith("image/") || f.type?.startsWith("video/") ||
        /\.(jpe?g|png|webp|gif|heic|heif|mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name)
    );
    const base = arr.map((file) => ({ file, status: "queued", id: null, preview: null, isVideo: isVideoFile(file), progress: 0 }));
    setItems((prev) => [...prev, ...base]);
    // Build tiny previews one-by-one so many large phone photos don't spike memory.
    for (const it of base) {
      if (it.isVideo) continue;
      // eslint-disable-next-line no-await-in-loop
      const thumb = await makeThumb(it.file);
      if (thumb) setItems((prev) => prev.map((p) => (p.file === it.file ? { ...p, preview: thumb } : p)));
    }
  };

  const applyCrop = async (croppedFile) => {
    const idx = cropIndex;
    setCropIndex(null);
    const thumb = await makeThumb(croppedFile);
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, file: croppedFile, preview: thumb || it.preview } : it)));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }, []);

  const uploadAll = async () => {
    setUploading(true);
    let ok = 0;
    const uploadedIds = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].status === "done") continue;
      setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: "uploading" } : it)));
      try {
        let data;
        if (items[i].isVideo) {
          data = await uploadVideoChunked(
            items[i].file,
            { privacy, album, reuse, sites: reuseSites() ? reuseSites().split(",") : [], makePublic },
            (pct) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, progress: pct } : it)))
          );
        } else {
          let fileToSend = items[i].file;
          const preset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.balanced;
          try { fileToSend = await resizeImageForUpload(items[i].file, preset.maxEdge, preset.q); } catch { /* use original */ }
          const fd = new FormData();
          fd.append("file", fileToSend);
          fd.append("privacy", privacy);
          fd.append("album", album);
          fd.append("reuse_permission", reuse);
          fd.append("usable_on_sites", reuseSites());
          fd.append("make_public", makePublic ? "true" : "false");
          const res = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          data = res.data;
        }
        setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: "done", id: data.id, dup: data.is_duplicate } : it)));
        uploadedIds.push(data.id);
        ok++;
      } catch (e) {
        setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, status: "error" } : it)));
      }
    }
    setUploading(false);
    toast.success(`${ok} file(s) imported. AI analysis is running in the background.`);
    if (reviewEach && uploadedIds.length) goGuidedQueue(uploadedIds);
  };

  const doneCount = items.filter((i) => i.status === "done").length;

  return (
    <div className="mx-auto max-w-4xl">
      <p className="overline text-[#F27A54]">Import</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Import Photos & Videos</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Drop images or videos from any device. Photos are auto-compressed (max 20 MB) and you can crop before upload;
        videos stream up in chunks. EXIF/GPS is read automatically and AI analysis runs in the background.
      </p>

      {/* Guided single-photo upload */}
      <Link to="/upload/guided" data-testid="guided-upload-cta"
        className="mt-5 flex items-center gap-4 rounded-lg border border-[#F27A54]/30 bg-gradient-to-r from-blue-50 to-white p-5 transition-colors hover:border-[#F27A54]">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[#F27A54] text-white">
          <Sparkle size={22} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold">Guided upload (one photo, full details)</h3>
          <p className="text-xs text-muted-foreground">AI drafts a title &amp; description from GPS, then you confirm location on a map, add tags &amp; notes.</p>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#F27A54]" />
      </Link>

      {/* Selective "Add from Photos" — group by month & place, pick what to import */}
      <Link to="/upload/photos" data-testid="selective-import-cta"
        className="mt-4 flex items-center gap-4 rounded-lg border border-[#2D6A4F]/30 bg-gradient-to-r from-emerald-50 to-white p-5 transition-colors hover:border-[#2D6A4F]">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[#2D6A4F] text-white">
          <ImageSquare size={22} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold">Add from your photos (grouped by month &amp; place)</h3>
          <p className="text-xs text-muted-foreground">Pick a big batch — we group it by date &amp; location so you tick only the sets worth keeping. Nothing uploads until you choose.</p>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#2D6A4F]" />
      </Link>
      <Card className="mt-5 rounded-md border border-border p-5 shadow-none" data-testid="url-import-card">
        <div className="flex items-center gap-2.5">
          <LinkSimple size={22} weight="bold" className="text-[#F27A54]" />
          <div>
            <h3 className="font-display text-sm font-bold">Import from a link</h3>
            <p className="text-xs text-muted-foreground">Paste a public photo or post URL. We fetch the image, run AI, then you finish the details.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
          <input
            data-testid="url-import-input"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doImport(); }}
            placeholder="https://example.com/photo.jpg or a public post link"
            className="h-10 min-w-[240px] flex-1 rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]"
          />
          <Button
            data-testid="url-import-btn"
            className="h-10 gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]"
            onClick={doImport}
            disabled={importing}
          >
            {importing ? <SpinnerGap size={15} weight="bold" className="animate-spin" /> : <ArrowSquareOut size={15} weight="bold" />}
            {importing ? "Importing…" : "Import link"}
          </Button>
        </div>
        {importNotice && importNotice.type === "wall" && (
          <div data-testid="url-import-wall" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="flex items-center gap-1.5 text-sm font-bold text-amber-800"><Warning size={16} weight="fill" /> Facebook / Instagram links can't be captured</p>
            <p className="mt-1 text-xs leading-relaxed text-amber-800">{importNotice.text}</p>
            <p className="mt-2 text-xs text-amber-800">
              <strong>To add your sunset photos:</strong> open the photo on Facebook → <em>Download</em> it to your device → then use the{" "}
              <Link to="/upload/guided" className="font-bold underline">Guided upload</Link> above, or the{" "}
              <Link to="/ingest" className="font-bold underline">Bulk Ingest</Link> tool for many at once. If they're in Google Photos, use the Google import below.
            </p>
          </div>
        )}
        {importNotice && importNotice.type === "error" && (
          <div data-testid="url-import-error" className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-700">{importNotice.text}</div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Facebook/Instagram posts are usually behind a login wall — if a link can't be fetched, download the photo and use the guided uploader instead.
        </p>
      </Card>

      <p className="mt-6 overline text-muted-foreground">Or bulk import many at once</p>

      {/* Google Photos import */}
      <Card className="mt-5 rounded-md border border-border p-5 shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <GoogleLogo size={22} weight="bold" className="text-[#4285F4]" />
            <div>
              <h3 className="font-display text-sm font-bold">Import from Google Photos</h3>
              <p className="text-xs text-muted-foreground">Admin connects their own Google account and hand-picks photos.</p>
            </div>
          </div>
          {!gp.configured ? (
            <span className="rounded-sm bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700" data-testid="gp-not-configured">Not configured yet</span>
          ) : gp.connected ? (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 rounded-sm bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><CheckCircle size={13} weight="fill" />Connected</span>
              <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={gpDisconnect}>Disconnect</Button>
            </div>
          ) : (
            <Button size="sm" className="h-9 gap-1.5 rounded-md bg-[#4285F4] hover:bg-[#3367d6]" onClick={gpConnect} data-testid="gp-connect-btn">
              <LinkSimple size={15} weight="bold" />Connect Google Photos
            </Button>
          )}
        </div>

        {gp.configured && gp.connected && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <Button className="h-9 gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" onClick={gpPick} disabled={gpBusy} data-testid="gp-pick-btn">
              <ArrowSquareOut size={15} weight="bold" />{gpBusy ? "Working…" : "Choose Photos in Google"}
            </Button>
            {gpSession && (
              <Button className="h-9 gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700" onClick={gpImport} disabled={gpBusy} data-testid="gp-import-btn">
                <UploadSimple size={15} weight="bold" />Import selected
              </Button>
            )}
            <span className="text-xs text-muted-foreground">Picked photos import with the privacy &amp; album set below. (Google strips GPS — AI infers location.)</span>
          </div>
        )}

        {!gp.configured && (
          <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            Add <code className="rounded bg-zinc-100 px-1">GOOGLE_CLIENT_ID</code> &amp; <code className="rounded bg-zinc-100 px-1">GOOGLE_CLIENT_SECRET</code> to the backend to enable this. Redirect URI: <code className="rounded bg-zinc-100 px-1 text-[10px]">{gp.redirect_uri}</code>
          </p>
        )}
      </Card>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div>
          <label className="overline mb-1 block text-[10px] text-muted-foreground">Default Privacy</label>
          <Select value={privacy} onValueChange={setPrivacy}>
            <SelectTrigger data-testid="upload-privacy" className="h-9 w-44 rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIVACY_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="overline mb-1 block text-[10px] text-muted-foreground">Album / Batch name</label>
          <input value={album} onChange={(e) => setAlbum(e.target.value)} placeholder="e.g. Kerala Trip 2026"
            data-testid="upload-album" className="h-9 w-56 rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
        </div>
        <div>
          <label className="overline mb-1 block text-[10px] text-muted-foreground">Upload quality</label>
          <Select value={quality} onValueChange={setQuality}>
            <SelectTrigger data-testid="upload-quality" className="h-9 w-56 rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(QUALITY_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Shared batch Rights & Reuse — applies to Google Photos + drag-drop bulk */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none" data-testid="batch-rights-card">
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={20} weight="fill" className="text-[#F27A54]" />
          <div>
            <h3 className="font-display text-sm font-bold">Rights &amp; reuse for this batch</h3>
            <p className="text-xs text-muted-foreground">Applied to every photo in this Google Photos import and drag-drop upload. Fine-tune individual photos later.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-4 border-t border-border pt-4">
          <label className="flex w-full cursor-pointer items-start gap-2.5 rounded-md border border-[#F27A54]/30 bg-blue-50/60 p-3" data-testid="make-public-toggle">
            <input type="checkbox" checked={makePublic} onChange={(e) => setMakePublic(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
            <span>
              <span className="block text-sm font-bold text-[#F27A54]">Make these photos public</span>
              <span className="mt-0.5 block text-xs text-zinc-600">
                Sets reuse to <b>public with attribution</b>. Photos first land in your private Library — add a <b>Title</b> (required), plus optional description, location, and notes, then hit <b>Publish to public gallery</b> so they appear on the web.
              </span>
            </span>
          </label>
          <div>
            <label className="overline mb-1 block text-[10px] text-muted-foreground">Reuse permission</label>
            <Select value={reuse} onValueChange={setReuse} disabled={makePublic}>
              <SelectTrigger data-testid="batch-reuse" className="h-9 w-64 rounded-md"><SelectValue /></SelectTrigger>
              <SelectContent>{REUSE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-1.5 text-sm font-medium text-zinc-700" data-testid="review-each-toggle">
            <input type="checkbox" checked={reviewEach} onChange={(e) => setReviewEach(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
            Review each photo in the guided editor after import
          </label>
        </div>
        {reuse !== "private" && reuse !== "family_only" && (
          <div className="mt-4" data-testid="batch-sites-group">
            <label className="overline mb-1.5 block text-[10px] text-muted-foreground">
              {reuse === "specific_sites" ? "Which sites can use these photos?" : "Prefer specific sites (optional)"}
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {SITES.map((s) => {
                const on = sites.includes(s.v);
                return (
                  <button type="button" key={s.v} data-testid={`batch-site-${s.v}`} onClick={() => toggleSite(s.v)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-semibold transition-colors ${
                      on ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-border bg-white text-zinc-600 hover:border-[#F27A54]/50"
                    }`}>
                    <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-sm border ${on ? "border-[#F27A54] bg-[#F27A54] text-white" : "border-zinc-300"}`}>
                      {on && <CheckCircle size={12} weight="fill" />}
                    </span>
                    {s.l}
                  </button>
                );
              })}
            </div>
            {reuse === "specific_sites" && !sites.length && (
              <p className="mt-2 text-xs text-amber-600">Pick at least one site, or these photos won't be reusable anywhere.</p>
            )}
          </div>
        )}
      </Card>

      {/* Dropzone */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          data-testid="take-photo-btn"
          onClick={() => cameraRef.current?.click()}
          className="flex items-center justify-center gap-2 rounded-md bg-[#F27A54] px-6 py-4 text-base font-bold text-white shadow-sm transition-colors duration-200 hover:bg-[#d9633f] sm:w-56"
        >
          <Camera size={22} weight="fill" /> Take a photo
        </button>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid="camera-input" onChange={(e) => addFiles(e.target.files)} />
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`grid flex-1 cursor-pointer place-items-center rounded-md border-2 border-dashed px-6 py-10 text-center transition-colors duration-200 ${
            dragging ? "border-[#F27A54] bg-blue-50/50" : "border-border bg-zinc-50 hover:border-[#F27A54]"
          }`}
        >
          <input ref={inputRef} type="file" multiple accept="image/*,video/*,.heic,.heif,.mp4,.mov,.m4v,.webm" className="hidden"
            data-testid="upload-input" onChange={(e) => addFiles(e.target.files)} />
          <UploadSimple size={32} weight="bold" className="text-[#F27A54]" />
          <p className="mt-2 font-display text-base font-bold sm:text-lg">Drop photos or videos, or tap to choose</p>
          <p className="text-xs text-muted-foreground">Phone-friendly · auto-compressed · photos ≤20MB · videos stream-uploaded · iPhone HEIC/MOV supported</p>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold">{items.length} file(s) · {doneCount} imported</p>
            <div className="flex gap-2">
              <Button variant="outline" className="h-9 rounded-md" onClick={() => setItems([])} disabled={uploading}>Clear</Button>
              <Button data-testid="upload-start-btn" className="h-9 rounded-md bg-[#F27A54] hover:bg-[#d9633f]"
                onClick={uploadAll} disabled={uploading || items.every((i) => i.status === "done")}>
                {uploading ? "Uploading…" : `Upload ${items.filter((i) => i.status !== "done").length}`}
              </Button>
              {doneCount > 0 && (
                <Button variant="outline" className="h-9 rounded-md" onClick={() => nav("/library")}>View Library</Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {items.map((it, i) => (
              <div key={i} className="relative overflow-hidden rounded-md border border-border" data-testid={`upload-item-${i}`}>
                {it.isVideo ? (
                  it.id ? (
                    <img src={imageUrl(it.id, "display")} alt="" loading="lazy" className="aspect-square w-full bg-zinc-900 object-cover" />
                  ) : (
                    <div className="grid aspect-square w-full place-items-center bg-zinc-900">
                      <FilmSlate size={30} weight="fill" className="text-white/70" />
                    </div>
                  )
                ) : (
                  <img src={it.id ? imageUrl(it.id, "display") : it.preview} alt="" loading="lazy" className="aspect-square w-full bg-zinc-100 object-cover" />
                )}
                <div className="absolute inset-0 grid place-items-center bg-black/40">
                  {it.status === "uploading" && (
                    <div className="text-center">
                      <SpinnerGap size={26} className="mx-auto animate-spin text-white" weight="bold" />
                      {it.isVideo && <span className="mt-1 block text-[10px] font-bold text-white">{it.progress || 0}%</span>}
                    </div>
                  )}
                  {it.status === "done" && (it.isVideo
                    ? <PlayCircle size={30} className="text-emerald-400" weight="fill" />
                    : <CheckCircle size={26} className="text-emerald-400" weight="fill" />)}
                  {it.status === "error" && <XCircle size={26} className="text-red-400" weight="fill" />}
                  {it.status === "queued" && <ImageSquare size={22} className="text-white/70" />}
                </div>
                {it.status === "queued" && !it.isVideo && (
                  <button type="button" data-testid={`crop-btn-${i}`} onClick={() => setCropIndex(i)}
                    className="absolute bottom-1 right-1 z-10 flex items-center gap-1 rounded-sm bg-black/70 px-2 py-1 text-[10px] font-bold text-white backdrop-blur-sm hover:bg-black/90">
                    <Crop size={12} weight="bold" /> Crop
                  </button>
                )}
                {it.isVideo && <span className="absolute left-1 top-1 rounded-sm bg-[#F27A54] px-1.5 py-0.5 text-[9px] font-bold text-white">VIDEO</span>}
                {it.dup && <span className="absolute left-1 top-1 rounded-sm bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">DUP</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {cropIndex !== null && items[cropIndex] && (
        <CropModal file={items[cropIndex].file} onCancel={() => setCropIndex(null)} onSave={applyCrop} />
      )}
    </div>
  );
}
